/**
 * The account pages' common parts (TF-ACCOUNT-ACCESS): the start-screen box the
 * login page uses, one labelled field, and the pure rules the tests cover: the
 * token in the query, the password rule, and the server's error codes in the
 * person's words.
 */
import { ApiError, get } from '@/api/client';
import { T } from '@/i18n';
import { useQuery } from '@tanstack/react-query';
import { type InputHTMLAttributes, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export const MIN_PASSWORD = 8;

/**
 * Whether these flows may be offered at all, from the server's public answer
 * (GET /account/availability). Two booleans, never a reason: what is configured is the
 * operator's business, and the screen only needs to know whether to invite somebody in.
 *
 * `unknown` while it is being read, and again when the read fails. A failure is not a
 * closed door: the flows may be perfectly open and the network may have blinked, so the
 * screens offer a retry rather than telling somebody the feature does not exist
 * (Codex, 2026-09-16).
 */
export interface Availability {
  signup: boolean;
  recovery: boolean;
  /** the sign-in providers that are on: 'google', 'microsoft' (2026-09-30) */
  sign_in_with?: string[];
}
export function useAvailability() {
  const q = useQuery({
    queryKey: ['account-availability'],
    queryFn: () => get<Availability>('/account/availability'),
    staleTime: 60_000,
    retry: 1,
  });
  return {
    signup: q.data?.signup,
    recovery: q.data?.recovery,
    signInWith: q.data?.sign_in_with ?? [],
    /** the read itself failed: unknown, and retryable, not "off" */
    unknown: q.isError,
    loading: q.isPending,
    retry: () => q.refetch(),
  };
}

/** `?token=` from a location search string; null when absent or empty. */
export function tokenFromQuery(search: string): string | null {
  const v = new URLSearchParams(search).get('token');
  return v ? v : null;
}

/** The client-side password rule; the server checks the same length. Null when fine. */
export function passwordProblem(password: string, confirm?: string): string | null {
  if (password.length < MIN_PASSWORD) return T('Passordet må ha minst 8 tegn.');
  if (confirm !== undefined && confirm !== password) return T('Passordene er ikke like.');
  return null;
}

/** The server's error strings (backend/controllers/account/accountController.js) as sentences. */
const KNOWN: Record<string, () => string> = {
  'A valid email is required': () => T('Skriv inn en gyldig e-postadresse.'),
  'Name and last name are required': () => T('Fornavn og etternavn må fylles ut.'),
  'Password must be at least 8 characters': () => T('Passordet må ha minst 8 tegn.'),
  'Too many requests. Try again later.': () => T('For mange forsøk. Vent litt og prøv igjen.'),
  'Company name must be 2 to 255 characters': () => T('Navnet på bedriften må ha mellom 2 og 255 tegn.'),
  name_taken: () => T('Navnet er allerede i bruk. Velg et annet.'),
  mail_unavailable: () => T('Vi kan ikke sende e-post akkurat nå. Prøv igjen senere.'),
  not_enabled: () => T('Registrering er ikke åpnet.'),
  invalid: () => T('Lenken er ikke gyldig.'),
  expired: () => T('Lenken har utløpt.'),
  used: () => T('Lenken er allerede brukt.'),
};
export function errorText(code: string, fallback?: string): string {
  return KNOWN[code]?.() ?? fallback ?? code;
}
/** What a failed request is shown as: a known code in words, else the server's own sentence, no answer as unknown. */
export function failureText(ex: unknown): string {
  if (ex instanceof ApiError) return errorText(ex.code ?? '', ex.message);
  return T('Fikk ikke svar fra tjeneren. Prøv igjen.');
}

/**
 * The same, for a flow that is not registration. `not_enabled` used to come back as
 * "Registrering er ikke åpnet." on the password page, which answers a question nobody
 * asked: somebody who has forgotten their password is not trying to register
 * (Codex, 2026-09-16).
 */
export function recoveryFailureText(ex: unknown): string {
  if (ex instanceof ApiError && ex.code === 'not_enabled') {
    return T(
      'Vi kan ikke sende nytt passord herfra ennå. Ta kontakt med lederen din, som kan sette et nytt passord for deg.',
    );
  }
  if (ex instanceof ApiError && ex.code === 'mail_unavailable') {
    return T('Vi kan ikke sende e-post akkurat nå. Prøv igjen senere, eller ta kontakt med lederen din.');
  }
  return failureText(ex);
}
/** The server's code from a refusal, or null when the failure carried none (network). */
/**
 * The demo seed a page was opened for (#128): «?demo=bygg» on the demo entry or the mailed link is the
 * trade firm with projects and no plan; anything else, or nothing, the café. Sent as the body's `demo`.
 */
export const seedFromQuery = (search: string): { demo?: 'bygg' } =>
  new URLSearchParams(search).get('demo') === 'bygg' ? { demo: 'bygg' } : {};

/**
 * Where a demo opens: a new one on the week grid, which the café calls Vaktplan (where Hugin's opening
 * question is about) and the trade firm, with planning off, calls «Uka» (#118); one seen before on the
 * home the shell picks (Hjem, or «Uka» with planning off).
 */
export const demoHome = (r: { new_demo?: unknown; demo?: unknown }) => (!r.new_demo ? '/' : '/vaktplan');

export const failureCode = (ex: unknown): string | null => (ex instanceof ApiError ? ex.code : null);

/**
 * Every account screen (login, signup, recovery, the new password, the address check, the demo and
 * company start): the owner's layout of 2026-09-27, the brand panel with the brush ring on the left and
 * the form on white on the right. Under 900px the panel goes and the wordmark sits over the form.
 */
export function KontoBox({
  title,
  sub,
  badge,
  children,
}: { title: string; sub?: string | undefined; badge?: string | undefined; children: ReactNode }) {
  return (
    <div className="auth">
      <aside className="auth-art" aria-hidden="true">
        <span className="auth-logo">timefly</span>
        <p className="auth-claim">
          {T('Mer tid.')}
          <br />
          {T('Mindre styr.')}
        </p>
      </aside>
      <main className="auth-main">
        <div className="auth-col">
          <span className="auth-logo auth-logo-small">timefly</span>
          {badge ? <span className="auth-badge">{badge}</span> : null}
          <h1>{title}</h1>
          {sub ? <p className="auth-sub">{sub}</p> : null}
          {children}
          <footer className="auth-foot">
            <a href="https://timefly.no/hjelp/" target="_blank" rel="noreferrer">
              {T('Hjelp')}
            </a>
            {' · '}
            <a href="https://timefly.no/privacy/">{T('Personvern')}</a>
          </footer>
        </div>
      </main>
    </div>
  );
}

export function Field({
  label,
  last: _last,
  ...rest
}: { label: string; last?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  const secret = rest.type === 'password';
  return (
    <label className="auth-fld">
      <span>{label}</span>
      <span className="auth-inp">
        <input {...rest} type={secret && shown ? 'text' : rest.type} />
        {secret ? (
          <button
            type="button"
            className="auth-eye"
            aria-label={shown ? T('Skjul tegnene') : T('Vis tegnene')}
            aria-pressed={shown}
            onClick={() => setShown(!shown)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
            >
              <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
              <circle cx="12" cy="12" r="3" />
              {shown ? <path d="M4 4l16 16" /> : null}
            </svg>
          </button>
        ) : null}
      </span>
    </label>
  );
}

export const hint: React.CSSProperties = { fontSize: 14, color: '#4a5668', textAlign: 'center' };

/**
 * A flow the server says is not open. It states the fact and what to do instead, and
 * offers the way back. It does not say why, because the reason is the operator's
 * configuration and nobody outside needs it (Codex, 2026-09-16).
 */
export function Unavailable({ what }: { what: string }) {
  return (
    <div className="opt" style={{ display: 'grid', gap: 10 }}>
      <output className="al info" style={{ margin: 0 }}>
        {what}
      </output>
      <Link to="/login" style={hint}>
        {T('Til innlogging')}
      </Link>
    </div>
  );
}

/**
 * Cloudflare Turnstile, the bot check (owner, 2026-09-27), since 2026-09-30 only where a demo link is opened:
 * the address is known by then, and the ads' in-app browsers had stopped people at the first form. It shows a box only when
 * it is unsure, and hands its token over through onToken; the server checks the token. Locally the
 * always-pass test key is used, since the real one is bound to timefly.no and app.timefly.no. A token
 * is single-use: remount (a new key) after a failed submit.
 */
const TURNSTILE_KEY =
  typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname)
    ? '1x00000000000000000000AA'
    : '0x4AAAAAAFFh3ByJf7a9_SdZ';
type Turnstile = {
  render: (el: HTMLElement, o: Record<string, unknown>) => string;
  remove: (id: string) => void;
};
let turnstileScript: Promise<Turnstile> | null = null;
function loadTurnstile(): Promise<Turnstile> {
  const w = window as unknown as { turnstile?: Turnstile };
  if (w.turnstile) return Promise.resolve(w.turnstile);
  // tests never reach Cloudflare; the server skips the check without its secret
  if (import.meta.env.MODE === 'test') return Promise.reject(new Error('no bot check in tests'));
  turnstileScript ??= new Promise((ok, fail) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => (w.turnstile ? ok(w.turnstile) : fail(new Error('turnstile missing')));
    s.onerror = () => {
      turnstileScript = null;
      fail(new Error('turnstile blocked'));
    };
    document.head.appendChild(s);
  });
  return turnstileScript;
}
export function BotCheck({ onToken }: { onToken: (token: string | null) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const cb = useRef(onToken);
  cb.current = onToken;
  useEffect(() => {
    let id: string | null = null;
    let gone = false;
    loadTurnstile()
      .then((t) => {
        if (gone || !box.current) return;
        id = t.render(box.current, {
          sitekey: TURNSTILE_KEY,
          language: 'nb',
          size: 'flexible',
          // shown only when Cloudflare is unsure about the visitor; a person usually sees nothing (2026-09-29:
          // two of three who opened the demo form left it, and the "Verifiserer" box looked like a hurdle)
          appearance: 'interaction-only',
          callback: (token: string) => cb.current(token),
          'expired-callback': () => cb.current(null),
          'error-callback': () => cb.current(null),
        });
      })
      .catch(() => {}); // blocked or offline: the server decides, and says so
    return () => {
      gone = true;
      const w = window as unknown as { turnstile?: Turnstile };
      if (id && w.turnstile) w.turnstile.remove(id);
    };
  }, []);
  return <div ref={box} className="auth-botcheck" />;
}
